/* src/components/pages/Perfil.jsx - Última Modificación: 03-01-2026 */
/* ----------------------------------------------------------------- */

import React, { useState } from 'react';
import { Typography, Button, TextField, Divider,
         Dialog, DialogContent, DialogTitle, Card,
         CardContent, Stack, useTheme, alpha, InputAdornment,
         Alert, DialogActions, Box } from '@mui/material';

import api from '../../services/api.js'; // Api Interceptor en vez de Axios

import { RiCloseLine, RiLock2Line, RiSave3Line,
         RiEyeLine, RiEyeOffLine, RiReplyAllFill } from 'react-icons/ri';

import { useAuth } from '../../context/AuthContext.jsx';
import { useFeedback } from '../../context/FeedbackContext.jsx';

const Perfil = ({ open, onClose }) => {

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const { user, logout } = useAuth();
  const { showFeedback } = useFeedback(); // Mensajes breves no bloqueantes

  const [editingPassword, setEditingPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [form, setForm] = useState({
    oldPassword: '',
    newPassword: '',
    repeatPassword: '',
  });

  /* Dialog para mensajes */
  const [validationDialog, setValidationDialog] = useState({
    open: false,
    message: '',
  });

  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    setError('');
  };

  /* Para el dialogo final de cambio de credenciales */
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);

  const handleClose = () => {
    setEditingPassword(false);
    setForm({
      oldPassword: '',
      newPassword: '',
      repeatPassword: '',
    });
    setError('');
    onClose();
  };

  /* 🔐 CAMBIO DE CONTRASEÑA */
  const handleSubmitPassword = async () => {
    const { oldPassword, newPassword, repeatPassword } = form;

    if (!oldPassword || !newPassword || !repeatPassword) {
      setValidationDialog({
        open: true,
        message: 'Todos los campos son obligatorios.',
      });
      return;
    }

    if (newPassword !== repeatPassword) {
      setValidationDialog({
        open: true,
        message: 'Las contraseñas no coinciden.',
      });
      return;
    }

    try {
      setLoading(true);

      console.log(
        '🔐 authToken en sessionStorage:',
        sessionStorage.getItem('authToken')
      );


      await api.post('/usuarios/cambiarcontrasena', {
        oldPassword,
        newPassword,
        repeatPassword,
      });

      setSuccessDialogOpen(true);

    } catch (err) {
      showFeedback({
        message: 'Hubo un problema al procesar la solicitud.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  /* RENDER */
  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') handleClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{ py: 1.2, px: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            Perfil de usuario
          </Typography>

          <Button
            onClick={handleClose}
            startIcon={<RiCloseLine />}
            size="small"
          >
            Cerrar
          </Button>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          backgroundColor: isDark ? '#202020' : '#E5E5E5',
        }}
      >
        <Card
          elevation={0}
          sx={{
            mt: 2,
            backgroundColor: isDark ? '#202020' : '#F0F0F0',
            borderRadius: 2,
            border: isDark
              ? '1px solid rgba(255,255,255,0.08)'
              : '1px solid rgba(0,0,0,0.12)',
          }}
        >
          <CardContent>
            {/* INFO USUARIO */}
            <Stack spacing={1.2}>
              <InfoRow label="Usuario" value={user?.usuario} />
              <InfoRow label="Nombre" value={user?.nombre} />
              <InfoRow label="Rol" value={user?.rol} />
              <InfoRow label="Tipo" value={user?.tipo} />
            </Stack>

            {!editingPassword && (
              <Button
                fullWidth
                sx={{ mt: 3 }}
                variant="contained"
                startIcon={<RiLock2Line />}
                onClick={() => setEditingPassword(true)}
              >
                Cambiar contraseña
              </Button>
            )}

            {editingPassword && (
              <>
                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle1" gutterBottom>
                  Nueva contraseña
                </Typography>

                {error && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <Stack spacing={2}>
                  <TextField
                    label="Contraseña actual"
                    type="password"
                    fullWidth
                    value={form.oldPassword}
                    onChange={handleChange('oldPassword')}
                  />

                  <TextField
                    label="Nueva contraseña"
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    value={form.newPassword}
                    onChange={handleChange('newPassword')}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <Button
                              onClick={() => setShowPassword(!showPassword)}
                              size="small"
                              tabIndex={-1}
                            >
                              {showPassword ? <RiEyeOffLine /> : <RiEyeLine />}
                            </Button>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />

                  <TextField
                    label="Repetir nueva contraseña"
                    type="password"
                    fullWidth
                    value={form.repeatPassword}
                    onChange={handleChange('repeatPassword')}
                  />
                </Stack>

                <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                  <Button
                    fullWidth
                    sx={actionButtonSx.primary}
                    onClick={handleSubmitPassword}
                    startIcon={<RiSave3Line />}
                    disabled={loading}
                  >
                    Guardar
                  </Button>

                  <Button
                    fullWidth
                    sx={actionButtonSx.secondary}
                    onClick={handleClose}
                    startIcon={<RiReplyAllFill />}
                  >
                    Cancelar
                  </Button>
                </Stack>
              </>
            )}
          </CardContent>
        </Card>
      </DialogContent>

      <Dialog
        open={validationDialog.open}
        onClose={() =>
          setValidationDialog({ open: false, message: '' })
        }
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          Atención
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2">
            {validationDialog.message}
          </Typography>
        </DialogContent>

        {/* 🔹 Separador */}
        <Divider />

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            justifyContent: 'flex-end',
          }}
        >
          <Button
            onClick={() =>
              setValidationDialog({ open: false, message: '' })
            }
            variant="contained"
          >
            Entendido
          </Button>
        </DialogActions>

        {/* 🏷️ Marca */}
        <Divider />

        <Box
          sx={{
            py: 1,
            textAlign: 'center',
            opacity: 0.65,
          }}
        >
          <Typography variant="caption">
            ▶️ Overwatch
          </Typography>
        </Box>
      </Dialog>

      <Dialog
        open={successDialogOpen}
        onClose={() => {}}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Contraseña actualizada
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2">
            Tu contraseña fue cambiada correctamente.
            <br />
            Por seguridad, debes iniciar sesión nuevamente con tus nuevas credenciales.
          </Typography>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              setSuccessDialogOpen(false);
              handleClose();
              logout();
            }}
          >
            Entendido
          </Button>
        </DialogActions>

        <Divider />

        <Box sx={{ py: 1, textAlign: 'center', opacity: 0.65 }}>
          <Typography variant="caption">
            ▶️ Overwatch
          </Typography>
        </Box>
      </Dialog>

    </Dialog>
  );
};

/* 🔹 SUBCOMPONENTE InfoRow */
const InfoRow = ({ label, value }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Stack direction="row" spacing={1}>
      <Typography
        fontWeight={600}
        width={120}
        sx={{
          color: isDark
            ? theme.palette.text.secondary
            : 'rgba(0,0,0,0.65)',
        }}
      >
        {label}:
      </Typography>

      <Typography
        sx={{
          color: alpha(
            isDark
              ? theme.palette.warning.light
              : theme.palette.warning.dark,
            isDark ? 0.85 : 0.8
          ),
          fontWeight: 500,
        }}
      >
        {value || '-'}
      </Typography>
    </Stack>
  );
};

/* 🔹 ESTILOS DE BOTONES */
const actionButtonSx = {
  primary: (theme) => {
    const isDark = theme.palette.mode === 'dark';
    const main = theme.palette.primary.main;

    return {
      backgroundColor: isDark ? alpha(main, 0.22) : main,
      color: isDark ? main : '#ffffff',
      fontWeight: 600,
      borderRadius: 8,
      boxShadow: isDark
        ? `0 0 0 1px ${alpha(main, 0.35)}`
        : '0 4px 12px rgba(37,99,235,0.25)',

      '&:hover': {
        backgroundColor: isDark ? alpha(main, 0.32) : '#1d4ed8',
        boxShadow: isDark
          ? `0 0 0 1px ${alpha(main, 0.6)}`
          : '0 6px 16px rgba(37,99,235,0.35)',
      },

      '&:active': {
        transform: 'translateY(1px)',
      },
    };
  },

  secondary: (theme) => {
    const isDark = theme.palette.mode === 'dark';

    return {
      backgroundColor: isDark ? '#2b2b2b' : '#E5E7EB',
      color: theme.palette.text.primary,
      fontWeight: 500,
      borderRadius: 8,
      border: isDark
        ? '1px solid rgba(255,255,255,0.16)'
        : '1px solid rgba(0,0,0,0.16)',

      '&:hover': {
        backgroundColor: isDark
          ? 'rgba(255,255,255,0.12)'
          : '#D1D5DB',
      },
    };
  },
};

export default Perfil;
